import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useLocation, useNavigate } from "react-router";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import type { AuthResponse, LoginRequest, RegisterRequest, User } from "@baze/api-client";
import { api, client, onSessionExpired } from "@/lib/api";
import { qk } from "@/lib/query";
import { t } from "@/i18n/id";
import { Splash } from "@/components/Splash";
import { loginPath } from "./redirect";

type Status = "loading" | "authed" | "anon";

interface AuthContextValue {
  status: Status;
  user: User | null;
  login: (b: LoginRequest) => Promise<User>;
  register: (b: RegisterRequest) => Promise<User>;
  logout: (opts?: { silent?: boolean; everywhere?: boolean }) => Promise<void>;
  /** Re-fetch /auth/me (after profile edits, verification, Google link). */
  reloadUser: () => Promise<void>;
  /** Complete an external login (Google callback): restore via refresh cookie. */
  completeExternalLogin: () => Promise<User | null>;
  setUser: (u: User) => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<Status>("loading");
  const [user, setUserState] = useState<User | null>(null);
  const qc = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();
  const locRef = useRef(location);
  locRef.current = location;

  const accept = useCallback(
    (auth: AuthResponse) => {
      client.setAccessToken(auth.accessToken);
      setUserState(auth.user);
      qc.setQueryData(qk.me, auth.user);
      setStatus("authed");
      return auth.user;
    },
    [qc],
  );

  const reset = useCallback(() => {
    client.setAccessToken(null);
    setUserState(null);
    setStatus("anon");
    qc.clear();
  }, [qc]);

  // Boot: restore the session from the HttpOnly refresh cookie.
  useEffect(() => {
    let alive = true;
    client.refresh().then((auth) => {
      if (!alive) return;
      if (auth) accept(auth);
      else setStatus("anon");
    });
    return () => {
      alive = false;
    };
  }, [accept]);

  useEffect(() => {
    onSessionExpired(() => {
      const { pathname, search } = locRef.current;
      reset();
      toast.info(t.auth.sessionExpired);
      navigate(loginPath(pathname + search), { replace: true });
    });
    return () => onSessionExpired(null);
  }, [navigate, reset]);

  const value = useMemo<AuthContextValue>(
    () => ({
      status,
      user,
      login: async (b) => accept(await api.auth.login(b)),
      register: async (b) => accept(await api.auth.register(b)),
      logout: async (opts) => {
        try {
          if (opts?.everywhere) await api.auth.logoutAll();
          await api.auth.logout();
        } catch {
          /* cookie may already be gone — local logout still proceeds */
        }
        reset();
        if (!opts?.silent) toast.success(t.auth.loggedOut);
      },
      reloadUser: async () => {
        const me = await api.auth.me();
        setUserState(me);
        qc.setQueryData(qk.me, me);
      },
      completeExternalLogin: async () => {
        const auth = await client.refresh();
        if (!auth) return null;
        return accept(auth);
      },
      setUser: (u) => {
        setUserState(u);
        qc.setQueryData(qk.me, u);
      },
    }),
    [status, user, accept, reset, qc],
  );

  return <AuthContext.Provider value={value}>{status === "loading" ? <Splash /> : children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}
